/**
 * authFetch — Auth-aware fetch wrapper (MADE-BUG-05)
 *
 * Attaches `Authorization: Bearer <token>` to ALL requests (GET and writes).
 * This is required because staff-only endpoints (e.g. /api/madebb/manufacturing-orders)
 * enforce requireStaff middleware on all HTTP methods, including GET.
 *
 * Behaviour:
 * - Calls authService.getIdToken() before each request.
 * - If getIdToken() fails on first attempt, retries once silently with forceRefresh=true.
 * - If no token is available (unauthenticated), request proceeds without auth header.
 * - If both token attempts fail, throws AuthFetchError so callers can redirect to /madebb/login.
 * - Enforces a 10-second AbortController timeout; throws on timeout.
 * - If the response is 401, throws AuthFetchError so the UI can handle re-auth.
 *
 * MADE-BUG-12: Fixed — GET requests now include auth token so requireStaff
 * middleware on manufacturing-orders and related endpoints does not 401.
 */

import { authService } from '../../services/authService';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const TIMEOUT_MS = 10_000;

export class AuthFetchError extends Error {
  constructor(
    message: string,
    public readonly type: 'unauthenticated' | 'token_refresh_failed' | 'timeout' | 'session_expired'
  ) {
    super(message);
    this.name = 'AuthFetchError';
  }
}

/**
 * Fetch a Firebase ID token with one silent retry on failure.
 * Throws AuthFetchError if both attempts fail.
 */
async function getTokenWithRetry(): Promise<string> {
  try {
    const token = await authService.getIdToken(false);
    if (token) return token;
  } catch {
    // fall through to retry
  }

  try {
    const token = await authService.getIdToken(true); // force refresh
    if (token) return token;
  } catch {
    // both failed
  }

  throw new AuthFetchError(
    'Your session could not be refreshed. Please sign in again.',
    'token_refresh_failed'
  );
}

/**
 * Drop-in replacement for `fetch` that automatically adds the Firebase ID token
 * to write requests. Read requests (GET, HEAD) bypass auth token injection.
 */
export async function authFetch(
  url: RequestInfo | URL,
  options: RequestInit = {}
): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  // Do NOT set Content-Type for FormData — browser must set it with the multipart boundary.
  // For all other requests default to application/json.
  const isFormData = options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...((options.headers as Record<string, string>) ?? {}),
  };

  // Attach token for all requests — staff-only GET endpoints (e.g. manufacturing-orders)
  // require requireStaff middleware which checks the Bearer token on every method.
  // MADE-BUG-12: was previously only attaching token for WRITE_METHODS.
  //
  // Write methods: throw if token cannot be obtained (can't proceed without auth).
  // Read methods: attach token if available; if unauthenticated proceed without token
  //               (server will return 401 and authFetch will throw AuthFetchError below).
  // LOCAL E2E BYPASS: when VITE_E2E_TEST_MODE is set, skip Firebase token entirely.
  // The server-side requireStaff middleware also bypasses auth in this mode.
  const isE2ETestMode = import.meta.env.VITE_E2E_TEST_MODE === 'true';

  const isWrite = WRITE_METHODS.has(method);
  if (!isE2ETestMode) {
    if (isWrite) {
      const token = await getTokenWithRetry();
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      try {
        const token = await getTokenWithRetry();
        headers['Authorization'] = `Bearer ${token}`;
      } catch {
        // Not authenticated — proceed without token; server will 401 if auth required
      }
    }
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (err: any) {
    clearTimeout(timer);
    if (err?.name === 'AbortError') {
      throw new AuthFetchError(
        'The request took too long. Please check your connection and try again.',
        'timeout'
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 401) {
    throw new AuthFetchError(
      'Session expired — please sign in again',
      'session_expired'
    );
  }

  return response;
}
